import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { HotToastService } from "@ngneat/hot-toast";
import { BsDropdownDirective, BsDropdownModule } from "ngx-bootstrap/dropdown";
import { TypeaheadMatch, TypeaheadModule } from "ngx-bootstrap/typeahead";
import { Subject, takeUntil } from "rxjs";
import { ChatService } from "src/app/dashboard/chat.service";
import { AccountantService } from "src/app/dashboard/main/accountant/accountant.service";
import { CurrentWorkspaceService } from "src/app/dashboard/main/accountant/current-workspace.service";
import { NotesService } from "src/app/dashboard/main/accountant/notes/notes.service";
import {
  DataToPinItem,
  getAppNameByModel,
  PinItemResponse,
  PinnedItem,
  PinnedModels,
} from "src/app/dashboard/main/accountant/pinned-panel/pinned-models";
import { PinnedService } from "src/app/dashboard/main/accountant/pinned-panel/pinned.service";
import {
  Company,
  SharedDataService,
  User,
} from "src/app/dashboard/shared-data.service";
import { AvatarComponent } from "src/app/design-system/components/avatar/avatar.component";

export const PINNED_STATUS_CHOICES = {
  TODO: 1,
  DONE: 2,
};

@Component({
  selector: "ds-pin",
  imports: [
    CommonModule,
    AvatarComponent,
    TypeaheadModule,
    BsDropdownModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: "./pin.component.html",
  styleUrl: "./pin.component.scss",
})
export class PinComponent {
  PINNED_STATUS_CHOICES = PINNED_STATUS_CHOICES;

  @Input() hovering = false;
  @Input() isMessage = false;
  @Input() type: PinnedModels = null;
  _company: Company;
  get company(): Company {
    return this._company;
  }
  @Input() set company(value: Company) {
    // check if value an object
    if (typeof value === "object") {
      this._company = value;
      if (value != null) {
        if (value.uuid == null) {
          this.getCompanyUuid(value);
        }
      }
    }
  }
  @Input() identifier: any;
  @Input() model: any; // pinnable object
  @Input() showAssignedTo: boolean = true;
  @Input() additionalAttributes: any;
  @Input() availableStaff = null;

  _pin: PinnedItem = null;
  get pin(): PinnedItem {
    return this._pin;
  }
  @Input() set pin(value) {
    this._pin = value;
    this.selectedUser = value?.assigned_to ?? { full_name: "" };
  }

  @ViewChild("dropdown") dropdownElem: BsDropdownDirective;

  @Output() pinActionCompleted = new EventEmitter<void>();

  unsubscribe$ = new Subject();

  currentUser: User;
  loading = false;
  inputValueHolder: string = "";
  selectedUser: User = { full_name: "" };

  readonly destroyRef = inject(DestroyRef);

  constructor(
    private accountantService: AccountantService,
    private cdr: ChangeDetectorRef,
    private chatService: ChatService,
    private pinnedService: PinnedService,
    private sharedDataService: SharedDataService,
    private notesService: NotesService,
    private toastr: HotToastService,
    public currentWorkspaceService: CurrentWorkspaceService,
  ) {}

  private async getCompanyUuid(value: Company) {
    let { uuid: companyUuid } = await this.notesService.getCompanyUuid(
      value.id,
    );

    this._company.uuid = companyUuid;
  }

  ngOnInit() {
    // TODO: move to dashboardStorage
    this.pinnedService.changePinStatus$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        if (this._pin != null) {
          if (res.pinUuid === this._pin.uuid) {
            this._pin.pinned_status = res.status;

            this.cdr.detectChanges();
          }
        }
      });

    if (
      this.type === PinnedModels.Message &&
      this.isPinned() &&
      ![null, undefined].includes(this.model.pinUuid)
    ) {
      this.getPin(this.company.uuid, this.model.pinUuid);
    }

    if (this._pin != null) {
      this.selectedUser = this._pin.assigned_to ?? { full_name: "" };
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  private getPin(companyUuid: string, pinUuid: string) {
    if (companyUuid == null) return;
    this.pinnedService
      .getPinnedItem(companyUuid, pinUuid)
      .subscribe((res: PinnedItem) => {
        this._pin = res;
        this.selectedUser = this._pin?.assigned_to ?? { full_name: "" }; // fallback
        this.cdr.detectChanges();
      });
  }

  createPin() {
    switch (this.type) {
      case PinnedModels.Message:
        return this._createMessagePin();
      default:
        return this._createRegularPin();
    }
  }

  deletePin() {
    switch (this.type) {
      case PinnedModels.Message:
        return this._deleteMessagePin();
      default:
        return this._deleteRegularPin();
    }
  }

  updatePinAssignee(event: TypeaheadMatch) {
    this.selectedUser = event.item;
    this.pinnedService
      .assignPin(this.company.uuid, this._pin.uuid, this.selectedUser.email)
      .subscribe({
        next: () => {
          this._pin.assigned_to = event.item;
          if (this.model.pinned_item != null) {
            this.model.pinned_item.assigned_to = event.item;
          }
          this.toastr.success("Pin successfully assigned");
        },
        error: () => this.toastr.error("Failed to assign pin"),
        complete: () => this.cdr.detectChanges(),
      });
  }

  updatePinStatus(statusCode: number) {
    switch (this.type) {
      case PinnedModels.Message:
        return this._updateMessagePinStatus(statusCode);
      default:
        return this._updateRegularPinStatus(statusCode);
    }
  }

  handlePinClick(e: Event) {
    e.stopPropagation();
    if (this.isPinned()) {
      return;
    }
    this.createPin();
  }

  _createRegularPin() {
    let pinData: DataToPinItem = {
      model_app_name: getAppNameByModel(this.type),
      model_name: this.type,
      object_pk: this.identifier,
    };
    this.pinnedService
      .pinItem(pinData, this.company.uuid)
      .then((res) => {
        this._pin = res.pinned_item;
        this.model.pinned_item = this._pin;
        this.pinnedService.newPinAdded$.next(res);
        this.toastr.success(res.message);
        this.pinActionCompleted.emit(); // Notify parent component
      })
      .catch((err) => {
        console.error(err);
        this.toastr.error("Failed to create pin");
      })
      .finally(() => this.cdr.detectChanges());
  }

  _updateRegularPinStatus(statusCode: number) {
    this.pinnedService
      .changePinStatus(this.company.uuid, this._pin.uuid, statusCode)
      .subscribe({
        next: () => {
          this._pin.pinned_status = statusCode;

          if (this.model.pinned_item != null) {
            this.model.pinned_item.pinned_status = statusCode;
          }

          this.pinnedService.changePinStatus$.next({
            pinUuid: this._pin.uuid,
            status: statusCode,
          });

          this.toastr.success("Pin status successfully updated");
        },
        error: () => this.toastr.error("Failed to update pin status"),
        complete: () => this.cdr.detectChanges(),
      });
  }

  _deleteRegularPin() {
    this.pinnedService.unpinItem(this.company.uuid, this._pin.uuid).subscribe({
      next: () => {
        this.pinnedService.unpinItemSubject.next(this._pin.uuid);
        this.pin = null;
        this.model.pinned_item = null;

        this.toastr.success("The item is no longer pinned");
        this.dropdownElem.toggle(false);
        this.pinActionCompleted.emit(); // Notify parent component
      },
      error: () => this.toastr.error("Failed to unpin item"),
      complete: () => this.cdr.detectChanges(),
    });
  }

  private _createMessagePin() {
    this.loading = true;

    this.chatService
      .pinMessage(
        this.model,
        this.additionalAttributes.conversationUuid,
        this.company.uuid,
        "pinned",
      )
      .then((_) =>
        this.pinnedService
          .getPinnedItem(this.company.uuid, this.model.pinUuid)
          .subscribe((res: PinnedItem) => {
            this._pin = res;
            this.pinnedService.newPinAdded$.next(res);
          }),
      )
      .catch((err) => console.error(err))
      .finally(() => {
        this.loading = false;
        this.cdr.detectChanges();
      });
  }

  private async _updateMessagePinStatus(statusCode: number) {
    this.loading = true;

    let pinStatus = null;
    if (statusCode == PINNED_STATUS_CHOICES.TODO) {
      pinStatus = "pinned";
    } else if (statusCode == PINNED_STATUS_CHOICES.DONE) {
      pinStatus = "completed";
    } else {
      pinStatus = "unpinned";
    }
    await this.chatService.pinMessageOnFirestore(
      this.additionalAttributes.conversationUuid,
      this.model.id || this.model.firestore_message_id,
      pinStatus,
    );
    this._updateRegularPinStatus(statusCode);

    this.cdr.detectChanges();
    this.loading = false;
  }

  private async _deleteMessagePin() {
    this.loading = true;

    this.chatService.pinMessageOnFirestore(
      this.additionalAttributes.conversationUuid,
      this.model.id || this.model.firestore_message_id,
      "unpinned",
    );

    this._deleteRegularPin();

    this.pinnedService.unpinItemSubject.next(this._pin.uuid);
    this.pinnedService.changePinStatus$.next({
      pinUuid: this._pin.uuid,
      status: null,
    });
    this.cdr.detectChanges();
    this.loading = false;
  }

  isPinned() {
    if (this.type == PinnedModels.Message) {
      return (
        this.model.pinned == true ||
        this.model.pinned == "pinned" ||
        this.model.pinned == "completed" ||
        [PINNED_STATUS_CHOICES.TODO, PINNED_STATUS_CHOICES.DONE].includes(
          this._pin?.pinned_status,
        )
      );
    }
    return (
      this._pin != null &&
      [PINNED_STATUS_CHOICES.TODO, PINNED_STATUS_CHOICES.DONE].includes(
        this._pin.pinned_status,
      )
    );
  }

  focus(event: FocusEvent) {
    this.inputValueHolder = (event.target as HTMLInputElement).value;
    (event.target as HTMLInputElement).value = "";
  }

  blur(event: FocusEvent) {
    let field = (event.target as HTMLInputElement).value;
    if (field === "") {
      (event.target as HTMLInputElement).value = this.inputValueHolder;
    }
    this.inputValueHolder = "";
  }
}
